import { createContext, useContext } from "react";

export interface TextLinkParams {
  vin?: string;
  pic?: string;
  make?: string;
  plate_encoded?: string;
  voucherCode?: string;
  [key: string]: string | boolean | undefined;
}

export const TextLinkParamsContext = createContext<TextLinkParams | null>(null);

export const useTextLinkParams = (): TextLinkParams | null => {
  return useContext(TextLinkParamsContext);
};

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

const CAMEL_TO_SNAKE_UTM: Record<string, string> = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmTerm: "utm_term",
  utmContent: "utm_content",
};

const CAMEL_TO_SNAKE_CT: Record<string, string> = {
  ctPm: "ct_pm",
  ctSt: "ct_st",
  ctTs: "ct_ts",
};

let storedUtmParams: Record<string, string> = {};
let storedCtParams: Record<string, string> = {};
let storedVin: string | null = null;
let storedUtmReferrer: string | null = null;
let storedKlinkId: string | null = null;

export const getStoredUtmParams = (): Record<string, string> => storedUtmParams;

const setStoredUtmParams = (params: Record<string, string>): void => {
  storedUtmParams = params;
};

export const clearStoredUtmParams = (): void => {
  storedUtmParams = {};
};

export const getStoredCtParams = (): Record<string, string> => storedCtParams;

const setStoredCtParams = (params: Record<string, string>): void => {
  storedCtParams = params;
};

export const clearStoredCtParams = (): void => {
  storedCtParams = {};
};

export const getStoredVin = (): string | null => storedVin;

const setStoredVin = (vin: string | null): void => {
  storedVin = vin;
};

export const clearStoredVin = (): void => {
  storedVin = null;
};

export const getStoredUtmReferrer = (): string | null => storedUtmReferrer;

export const setStoredUtmReferrer = (url: string): void => {
  storedUtmReferrer = url;
};

export const getStoredKlinkId = (): string | null => storedKlinkId;

export const setStoredKlinkId = (id: string): void => {
  storedKlinkId = id;
};

const buildUtmParams = (textLinkParams: TextLinkParams): Record<string, string> => {
  const params: Record<string, string> = {};

  for (const [camelKey, snakeKey] of Object.entries(CAMEL_TO_SNAKE_UTM)) {
    const camelValue = textLinkParams[camelKey];
    if (typeof camelValue === "string" && camelValue) {
      params[snakeKey] = camelValue;
      continue;
    }
    const snakeValue = textLinkParams[snakeKey];
    if (typeof snakeValue === "string" && snakeValue) {
      params[snakeKey] = snakeValue;
    }
  }

  return params;
};

const buildCtParams = (textLinkParams: TextLinkParams): Record<string, string> => {
  const params: Record<string, string> = {};

  for (const [camelKey, snakeKey] of Object.entries(CAMEL_TO_SNAKE_CT)) {
    const camelValue = textLinkParams[camelKey];
    if (typeof camelValue === "string" && camelValue) {
      params[snakeKey] = camelValue;
      continue;
    }
    const snakeValue = textLinkParams[snakeKey];
    if (typeof snakeValue === "string" && snakeValue) {
      params[snakeKey] = snakeValue;
    }
  }

  for (const key of Object.keys(textLinkParams)) {
    if (key.startsWith("ct_") && !params[key]) {
      const value = textLinkParams[key];
      if (typeof value === "string" && value) {
        params[key] = value;
      }
    }
  }

  return params;
};

export const syncTextLinkParams = (textLinkParams: TextLinkParams | null): void => {
  if (textLinkParams) {
    setStoredUtmParams(buildUtmParams(textLinkParams));
    setStoredCtParams(buildCtParams(textLinkParams));
    if (typeof textLinkParams.vin === "string") {
      setStoredVin(textLinkParams.vin);
    } else if (typeof textLinkParams.plate_encoded === "string") {
      setStoredVin(textLinkParams.plate_encoded);
    } else {
      setStoredVin(null);
    }
  }
};
